<!--
  PayRefundDailyView.vue — 收退款-财务页面
  按日期+维度统计收款退款日报表。
  功能：日期范围筛选、维度筛选、分页查询、CSV 下载。
  接口：GET /balance/order/stats/pay-refund/daily/report
-->
<template>
  <div>
    <!-- 搜索栏 -->
    <a-card :bordered="false" style="margin-bottom: 16px">
      <a-form layout="inline" :model="searchForm">
        <a-form-item label="开始日期">
          <a-date-picker v-model:value="searchForm.startDate" value-format="YYYY-MM-DD" :allow-clear="true"
            placeholder="开始日期" style="width: 140px" @change="clearQuickDate" />
        </a-form-item>
        <a-form-item label="结束日期">
          <a-date-picker v-model:value="searchForm.endDate" value-format="YYYY-MM-DD" :allow-clear="true"
            placeholder="结束日期" style="width: 140px" @change="clearQuickDate" />
        </a-form-item>
        <a-form-item label="快捷日期">
          <a-radio-group v-model:value="quickDateValue" option-type="button" button-style="solid"
            class="quick-date-group" @change="handleQuickDateChange">
            <a-radio-button v-for="item in quickDateOptions" :key="item.value" :value="item.value">
              {{ item.label }}
            </a-radio-button>
          </a-radio-group>
        </a-form-item>
        <a-form-item label="维度">
          <a-select v-model:value="searchForm.dimensionName" style="width: 320px" placeholder="全部维度" allow-clear
            :options="dimensionOptions" @change="handleSearch" />
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button type="primary" :loading="loading" @click="handleSearch">查询</a-button>
            <a-button @click="handleReset">重置</a-button>
            <a-button :loading="downloading" @click="handleDownload">下载</a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <!-- 数据表格 -->
    <a-card :bordered="false" title="收退款-财务">
      <a-table :columns="columns" :data-source="tableData" :loading="loading" :pagination="pagination" row-key="id"
        :scroll="{ x: 'max-content' }" size="small" bordered @change="handleTableChange">
        <template #summary>
          <a-table-summary fixed>
            <a-table-summary-row style="font-weight: 600; background: #fafafa">
              <a-table-summary-cell :index="0" :col-span="2" align="center">合计</a-table-summary-cell>
              <a-table-summary-cell :index="2" align="center">{{ totalRow.payOrderCount }}</a-table-summary-cell>
              <a-table-summary-cell :index="3" align="center">{{ totalRow.payAmount }}</a-table-summary-cell>
              <a-table-summary-cell :index="4" align="center">{{ totalRow.refundOrderCount }}</a-table-summary-cell>
              <a-table-summary-cell :index="5" align="center">{{ totalRow.refundAmount }}</a-table-summary-cell>
              <a-table-summary-cell :index="6" align="center">{{ totalRow.refundRate }}</a-table-summary-cell>
              <a-table-summary-cell :index="7" align="center">{{ totalRow.adminRefundOrderCount
                }}</a-table-summary-cell>
              <a-table-summary-cell :index="8" align="center">{{ totalRow.adminRefundAmount }}</a-table-summary-cell>
              <a-table-summary-cell :index="9" align="center">{{ totalRow.adminRefundRate }}</a-table-summary-cell>
              <a-table-summary-cell :index="10" align="center">{{ totalRow.h5RefundOrderCount }}</a-table-summary-cell>
              <a-table-summary-cell :index="11" align="center">{{ totalRow.h5RefundAmount }}</a-table-summary-cell>
              <a-table-summary-cell :index="12" align="center">{{ totalRow.h5RefundRate }}</a-table-summary-cell>
              <a-table-summary-cell :index="13" align="center">{{ totalRow.taojinRefundOrderCount
                }}</a-table-summary-cell>
              <a-table-summary-cell :index="14" align="center">{{ totalRow.taojinRefundAmount }}</a-table-summary-cell>
              <a-table-summary-cell :index="15" align="center">{{ totalRow.taojinRefundRate }}</a-table-summary-cell>
              <a-table-summary-cell :index="16" align="center">{{ totalRow.mengyingRefundOrderCount
                }}</a-table-summary-cell>
              <a-table-summary-cell :index="17" align="center">{{ totalRow.mengyingRefundAmount
                }}</a-table-summary-cell>
              <a-table-summary-cell :index="18" align="center">{{ totalRow.mengyingRefundRate }}</a-table-summary-cell>
              <a-table-summary-cell :index="19" align="center">-</a-table-summary-cell>
            </a-table-summary-row>
          </a-table-summary>
        </template>
      </a-table>
    </a-card>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { message } from "ant-design-vue";
import { analyticsUrl } from "@/utils/api";
import { get } from "@/utils/request";

/* ---------- 格式化工具 ---------- */
const formatAmount = (val) => {
  if (val == null) return "-";
  return (
    "¥" +
    (val / 100).toLocaleString("zh-CN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
};
const formatRate = (val) => {
  if (val == null) return "-";
  return (parseFloat(val) * 100).toFixed(2) + "%";
};

const dimensionDisplayOrder = [
  "ali单次-承接支付宝",
  "英特布尔-快钱一起看",
  "英特布尔-快钱一起看-回捞",
  "云海-快钱一起看-回捞",
  "云海-快钱一起看",
  "英特布尔-快钱aimax-停车场",
  "英特布尔-快钱aimax-停车场-回捞",
  "英特布尔-快钱琼岸智-建行-回捞",
  "英特布尔-快钱琼岸智-建行",
  "云海-快钱琼岸智-建行-回捞",
  "云海-快钱琼岸智-建行",
  "英特布尔-快钱-停车场",
  "英特布尔-快钱-停车场-回捞",
  "英特布尔-合利宝-回捞",
  "英特布尔-合利宝",
  "权益卡-云海-合利宝-回捞",
  "权益卡-云海-合利宝",
  "英特布尔-合利宝-高价",
  "英特布尔-合利宝-高价-回捞",
  "英特布尔-ZY合利宝高价",
  "英特布尔-ZY合利宝高价回捞",
  "英特布尔-合利宝停车场快钱",
  "英特布尔-合利宝停车场快钱-回捞",
  "英特布尔-快钱-单单",
  "英特布尔-快钱-单双",
  "英特布尔-快钱新通-回捞",
  "英特布尔-快钱新通",
  "天卓-快钱新通建行",
  "天卓-快钱新通建行-回捞",
  "刘元博-快钱新通建行-回捞",
  "刘元博-快钱新通建行",
  "英特布尔-合利宝停车场新通",
  "英特布尔-合利宝停车场新通-回捞",
  // "英特布尔-合利宝-停车场",
  // "英特布尔-合利宝-停车场-回捞",
  "云海-玉笙线下",
  "云海-玉笙线下-回捞",
  "云海-快钱视界-回捞",
  "云海-快钱视界",
  "英特布尔-wj快钱-高价",
  "英特布尔-wj快钱-高价-回捞",
  "英特布尔-wy合利宝-视界高价",
  "英特布尔-wy合利宝-视界高价-回捞",
  "英特布尔-cj合利宝-视界高价",
  "英特布尔-cj合利宝-视界高价-回捞",
  "英特布尔-wj合利宝-视界高价",
  "英特布尔-wj合利宝-视界高价-回捞",
  "英特布尔-cj合利宝-新通高价",
  "英特布尔-cj合利宝-新通高价-回捞",
  "英特布尔-wj合利宝-新通高价",
  "英特布尔-wj合利宝-新通高价-回捞",
  "英特布尔-wy合利宝-新通高价",
  "英特布尔-wy合利宝-新通高价-回捞",
  "英特布尔-wy合利宝-聚龙新通高价",
  "英特布尔-wy合利宝-聚龙新通高价-回捞",
  "天卓-快钱一起看",
  "天卓-快钱一起看-回捞",
  "刘元博-快钱一起看-回捞",
  "刘元博-快钱一起看",
];

const dimensionDisplayOrderMap = new Map(
  dimensionDisplayOrder.map((name, index) => [name, index])
);

const getDimensionOrder = (dimensionName) => {
  const order = dimensionDisplayOrderMap.get(dimensionName);
  return order == null ? Number.MAX_SAFE_INTEGER : order;
};

const sortRowsByDateAndDimension = (rows) =>
  [...rows].sort((left, right) => {
    const dateCompare = String(right.statDate ?? "").localeCompare(
      String(left.statDate ?? "")
    );

    if (dateCompare !== 0) {
      return dateCompare;
    }

    const dimensionCompare =
      getDimensionOrder(left.dimensionName) -
      getDimensionOrder(right.dimensionName);

    if (dimensionCompare !== 0) {
      return dimensionCompare;
    }

    return String(left.dimensionName ?? "").localeCompare(
      String(right.dimensionName ?? ""),
      "zh-CN"
    );
  });

const zeroNumericFields = [
  "payOrderCount",
  "payAmount",
  "refundOrderCount",
  "refundAmount",
  "adminRefundOrderCount",
  "adminRefundAmount",
  "h5RefundOrderCount",
  "h5RefundAmount",
  "taojinRefundOrderCount",
  "taojinRefundAmount",
  "mengyingRefundOrderCount",
  "mengyingRefundAmount",
];

const fillMissingDimensions = (rows) => {
  // 当筛选了单个维度时，不需要补全其他维度的空行
  if (searchForm.dimensionName) return rows;

  const dateSet = new Set(rows.map((r) => r.statDate));
  const existingKeys = new Set(
    rows.map((r) => `${r.statDate}|${r.dimensionName}`)
  );

  const filled = [...rows];
  for (const date of dateSet) {
    for (const dim of dimensionDisplayOrder) {
      if (!existingKeys.has(`${date}|${dim}`)) {
        const emptyRow = { statDate: date, dimensionName: dim, updatedAt: null };
        for (const field of zeroNumericFields) {
          emptyRow[field] = 0;
        }
        // 零比率字段留 null，让 formatRate 显示 "-"
        emptyRow.refundRate = null;
        emptyRow.adminRefundRate = null;
        emptyRow.h5RefundRate = null;
        emptyRow.taojinRefundRate = null;
        emptyRow.mengyingRefundRate = null;
        filled.push(emptyRow);
      }
    }
  }
  return filled;
};

/* ---------- 维度选项（固定列表） ---------- */
const dimensionOptions = [
  {
    label: "ali单次-承接支付宝",
    value: "ali单次-承接支付宝"
  }, {
    label: "云海-快钱一起看",
    value: "云海-快钱一起看"
  },
  {
    label: "云海-快钱一起看-回捞",
    value: "云海-快钱一起看-回捞"
  },
  {
    label: "云海-快钱琼岸智-建行",
    value: "云海-快钱琼岸智-建行"
  },
  {
    label: "云海-快钱琼岸智-建行-回捞",
    value: "云海-快钱琼岸智-建行-回捞"
  },
  {
    label: "云海-快钱视界",
    value: "云海-快钱视界"
  },
  {
    label: "云海-快钱视界-回捞",
    value: "云海-快钱视界-回捞"
  },
  {
    label: "云海-玉笙线下",
    value: "云海-玉笙线下"
  },
  {
    label: "云海-玉笙线下-回捞",
    value: "云海-玉笙线下-回捞"
  },
  {
    label: "刘元博-快钱一起看",
    value: "刘元博-快钱一起看"
  },
  {
    label: "刘元博-快钱一起看-回捞",
    value: "刘元博-快钱一起看-回捞"
  },
  {
    label: "刘元博-快钱新通建行",
    value: "刘元博-快钱新通建行"
  },
  {
    label: "刘元博-快钱新通建行-回捞",
    value: "刘元博-快钱新通建行-回捞"
  },
  {
    label: "天卓-快钱一起看",
    value: "天卓-快钱一起看"
  },
  {
    label: "天卓-快钱一起看-回捞",
    value: "天卓-快钱一起看-回捞"
  },
  {
    label: "天卓-快钱新通建行",
    value: "天卓-快钱新通建行"
  },
  {
    label: "天卓-快钱新通建行-回捞",
    value: "天卓-快钱新通建行-回捞"
  },
  {
    label: "权益卡-云海-合利宝",
    value: "权益卡-云海-合利宝"
  },
  {
    label: "权益卡-云海-合利宝-回捞",
    value: "权益卡-云海-合利宝-回捞"
  },

  {
    label: "英特布尔-合利宝",
    value: "英特布尔-合利宝"
  },
  {
    label: "英特布尔-合利宝停车场新通",
    value: "英特布尔-合利宝停车场新通"
  },
  {
    label: "英特布尔-合利宝停车场新通-回捞",
    value: "英特布尔-合利宝停车场新通-回捞"
  },
  {
    label: "英特布尔-合利宝停车场快钱",
    value: "英特布尔-合利宝停车场快钱"
  },
  {
    label: "英特布尔-合利宝停车场快钱-回捞",
    value: "英特布尔-合利宝停车场快钱-回捞"
  },
  {
    label: "英特布尔-合利宝-回捞",
    value: "英特布尔-合利宝-回捞"
  },
  {
    label: "英特布尔-快钱-单单",
    value: "英特布尔-快钱-单单"
  },
  {
    label: "英特布尔-快钱-单单-回捞",
    value: "英特布尔-快钱-单单-回捞"
  },
  {
    label: "英特布尔-快钱-单双",
    value: "英特布尔-快钱-单双"
  },
  {
    label: "英特布尔-快钱-单双-回捞",
    value: "英特布尔-快钱-单双-回捞"
  },
  {
    label: "英特布尔-快钱一起看",
    value: "英特布尔-快钱一起看"
  },
  {
    label: "英特布尔-快钱一起看-回捞",
    value: "英特布尔-快钱一起看-回捞"
  },
  {
    label: "英特布尔-快钱新通",
    value: "英特布尔-快钱新通"
  },
  {
    label: "英特布尔-快钱新通-回捞",
    value: "英特布尔-快钱新通-回捞"
  },
  {
    label: "英特布尔-快钱琼岸智-建行",
    value: "英特布尔-快钱琼岸智-建行"
  },
  {
    label: "英特布尔-快钱琼岸智-建行-回捞",
    value: "英特布尔-快钱琼岸智-建行-回捞"
  },
  {
    label: "英特布尔-合利宝-高价",
    value: "英特布尔-合利宝-高价"
  },
  {
    label: "英特布尔-合利宝-高价-回捞",
    value: "英特布尔-合利宝-高价-回捞"
  },
  {
    label: "英特布尔-ZY合利宝高价",
    value: "英特布尔-ZY合利宝高价"
  },
  {
    label: "英特布尔-ZY合利宝高价回捞",
    value: "英特布尔-ZY合利宝高价回捞"
  },
  {
    label: "英特布尔-快钱-停车场",
    value: "英特布尔-快钱-停车场"
  },
  {
    label: "英特布尔-快钱-停车场-回捞",
    value: "英特布尔-快钱-停车场-回捞"
  },
  {
    label: "英特布尔-wj快钱-高价",
    value: "英特布尔-wj快钱-高价"
  },
  {
    label: "英特布尔-wj快钱-高价-回捞",
    value: "英特布尔-wj快钱-高价-回捞"
  },
  {
    label: "英特布尔-wy合利宝-视界高价",
    value: "英特布尔-wy合利宝-视界高价"
  },
  {
    label: "英特布尔-wy合利宝-视界高价-回捞",
    value: "英特布尔-wy合利宝-视界高价-回捞"
  },
  {
    label: "英特布尔-cj合利宝-视界高价",
    value: "英特布尔-cj合利宝-视界高价"
  },
  {
    label: "英特布尔-cj合利宝-视界高价-回捞",
    value: "英特布尔-cj合利宝-视界高价-回捞"
  },
  {
    label: "英特布尔-wj合利宝-视界高价",
    value: "英特布尔-wj合利宝-视界高价"
  },
  {
    label: "英特布尔-wj合利宝-视界高价-回捞",
    value: "英特布尔-wj合利宝-视界高价-回捞"
  },
  {
    label: "英特布尔-cj合利宝-新通高价",
    value: "英特布尔-cj合利宝-新通高价"
  },
  {
    label: "英特布尔-cj合利宝-新通高价-回捞",
    value: "英特布尔-cj合利宝-新通高价-回捞"
  },
  {
    label: "英特布尔-wj合利宝-新通高价",
    value: "英特布尔-wj合利宝-新通高价"
  },
  {
    label: "英特布尔-wj合利宝-新通高价-回捞",
    value: "英特布尔-wj合利宝-新通高价-回捞"
  },
  {
    label: "英特布尔-wy合利宝-新通高价",
    value: "英特布尔-wy合利宝-新通高价"
  },
  {
    label: "英特布尔-wy合利宝-新通高价-回捞",
    value: "英特布尔-wy合利宝-新通高价-回捞"
  },
  {
    label: "英特布尔-wy合利宝-聚龙新通高价",
    value: "英特布尔-wy合利宝-聚龙新通高价"
  },
  {
    label: "英特布尔-wy合利宝-聚龙新通高价-回捞",
    value: "英特布尔-wy合利宝-聚龙新通高价-回捞"
  },
  {
    label: "权益卡-回捞-总",
    value: "权益卡-回捞-总"
  }
];

const quickDateOptions = [
  { label: "今天", value: "today", startOffset: 0, endOffset: 0 },
  { label: "昨天", value: "yesterday", startOffset: 1, endOffset: 1 },
  { label: "最近2天", value: "last_2_days", startOffset: 1, endOffset: 0 },
  { label: "最近3天", value: "last_3_days", startOffset: 2, endOffset: 0 },
  { label: "最近5天", value: "last_5_days", startOffset: 4, endOffset: 0 },
  { label: "最近7天", value: "last_7_days", startOffset: 6, endOffset: 0 },
  { label: "最近30天", value: "last_30_days", startOffset: 30, endOffset: 0 },
];

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDaysAgoDate = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return formatDate(date);
};

/* ---------- 状态 ---------- */
const loading = ref(false);
const downloading = ref(false);
const tableData = ref([]);
const quickDateValue = ref(null);

const searchForm = reactive({
  startDate: "",
  endDate: "",
  dimensionName: undefined,
});

const pagination = reactive({
  current: 1,
  pageSize: 50,
  total: 0,
  showSizeChanger: true,
  showQuickJumper: true,
  pageSizeOptions: ["50", "100", "200"],
  showTotal: (total) => `共 ${total} 条`,
});

/* ---------- 列定义 ---------- */
const nowrap = { style: { whiteSpace: "nowrap" } };

const columns = computed(() => [
  {
    title: "日期",
    dataIndex: "statDate",
    key: "statDate",
    width: 110,
    fixed: "left",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
  },
  {
    title: "维度",
    dataIndex: "dimensionName",
    key: "dimensionName",
    width: 160,
    fixed: "left",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
  },
  {
    title: "收款单数",
    dataIndex: "payOrderCount",
    key: "payOrderCount",
    width: 100,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => text?.toLocaleString() ?? "-",
  },
  {
    title: "收款金额",
    dataIndex: "payAmount",
    key: "payAmount",
    width: 120,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => formatAmount(text),
  },
  {
    title: "退款单数",
    dataIndex: "refundOrderCount",
    key: "refundOrderCount",
    width: 100,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => text?.toLocaleString() ?? "-",
  },
  {
    title: "退款金额",
    dataIndex: "refundAmount",
    key: "refundAmount",
    width: 120,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => formatAmount(text),
  },
  {
    title: "退款率",
    dataIndex: "refundRate",
    key: "refundRate",
    width: 90,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => formatRate(text),
  },
  {
    title: "小瑞退款单数",
    dataIndex: "adminRefundOrderCount",
    key: "adminRefundOrderCount",
    width: 120,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => text?.toLocaleString() ?? "-",
  },
  {
    title: "小瑞退款金额",
    dataIndex: "adminRefundAmount",
    key: "adminRefundAmount",
    width: 120,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => formatAmount(text),
  },
  {
    title: "小瑞退款率",
    dataIndex: "adminRefundRate",
    key: "adminRefundRate",
    width: 100,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => formatRate(text),
  },
  {
    title: "H5退款单数",
    dataIndex: "h5RefundOrderCount",
    key: "h5RefundOrderCount",
    width: 110,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => text?.toLocaleString() ?? "-",
  },
  {
    title: "H5退款金额",
    dataIndex: "h5RefundAmount",
    key: "h5RefundAmount",
    width: 120,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => formatAmount(text),
  },
  {
    title: "H5退款率",
    dataIndex: "h5RefundRate",
    key: "h5RefundRate",
    width: 90,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => formatRate(text),
  },
  {
    title: "淘金退款单数",
    dataIndex: "taojinRefundOrderCount",
    key: "taojinRefundOrderCount",
    width: 120,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => text?.toLocaleString() ?? "-",
  },
  {
    title: "淘金退款金额",
    dataIndex: "taojinRefundAmount",
    key: "taojinRefundAmount",
    width: 120,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => formatAmount(text),
  },
  {
    title: "淘金退款率",
    dataIndex: "taojinRefundRate",
    key: "taojinRefundRate",
    width: 100,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => formatRate(text),
  },
  {
    title: "梦莹退款单数",
    dataIndex: "mengyingRefundOrderCount",
    key: "mengyingRefundOrderCount",
    width: 120,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => text?.toLocaleString() ?? "-",
  },
  {
    title: "梦莹退款金额",
    dataIndex: "mengyingRefundAmount",
    key: "mengyingRefundAmount",
    width: 120,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => formatAmount(text),
  },
  {
    title: "梦莹退款率",
    dataIndex: "mengyingRefundRate",
    key: "mengyingRefundRate",
    width: 100,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => formatRate(text),
  },
  {
    title: "更新时间",
    dataIndex: "updatedAt",
    key: "updatedAt",
    width: 160,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
  },
]);

/* ---------- 合计行 ---------- */
const totalRow = computed(() => {
  const data = tableData.value;
  const sum = (key) =>
    data.reduce((acc, row) => acc + (Number(row[key]) || 0), 0);

  const payAmount = sum("payAmount");
  const refundAmount = sum("refundAmount");
  const adminRefundAmount = sum("adminRefundAmount");
  const h5RefundAmount = sum("h5RefundAmount");
  const taojinRefundAmount = sum("taojinRefundAmount");
  const mengyingRefundAmount = sum("mengyingRefundAmount");

  return {
    payOrderCount: sum("payOrderCount").toLocaleString(),
    payAmount: formatAmount(payAmount),
    refundOrderCount: sum("refundOrderCount").toLocaleString(),
    refundAmount: formatAmount(refundAmount),
    refundRate: payAmount ? formatRate(refundAmount / payAmount) : "-",
    adminRefundOrderCount: sum("adminRefundOrderCount").toLocaleString(),
    adminRefundAmount: formatAmount(adminRefundAmount),
    adminRefundRate: refundAmount
      ? formatRate(adminRefundAmount / refundAmount)
      : "-",
    h5RefundOrderCount: sum("h5RefundOrderCount").toLocaleString(),
    h5RefundAmount: formatAmount(h5RefundAmount),
    h5RefundRate: refundAmount
      ? formatRate(h5RefundAmount / refundAmount)
      : "-",
    taojinRefundOrderCount: sum("taojinRefundOrderCount").toLocaleString(),
    taojinRefundAmount: formatAmount(taojinRefundAmount),
    taojinRefundRate: refundAmount
      ? formatRate(taojinRefundAmount / refundAmount)
      : "-",
    mengyingRefundOrderCount: sum("mengyingRefundOrderCount").toLocaleString(),
    mengyingRefundAmount: formatAmount(mengyingRefundAmount),
    mengyingRefundRate: refundAmount
      ? formatRate(mengyingRefundAmount / refundAmount)
      : "-",
  };
});

/* ---------- 数据获取 ---------- */
const fetchData = async (pageIdx = 1, pageSize = 50) => {
  loading.value = true;
  try {
    const params = {
      pageIdx,
      pageSize,
      startDate: searchForm.startDate || "",
      endDate: searchForm.endDate || "",
    };
    if (searchForm.dimensionName)
      params.dimensionName = searchForm.dimensionName;
    const json = await get(
      analyticsUrl("/order/stats/pay-refund/daily/report"),
      params
    );
    if (json.code === 200) {
      const d = json.data;
      tableData.value = sortRowsByDateAndDimension(fillMissingDimensions(d.data ?? []));
      pagination.total = d.dataSum;
      pagination.current = d.pageIdx;
      pagination.pageSize = d.pageSize;
    } else {
      message.warning("查询失败：" + (json.msg || json.content || "未知错误"));
    }
  } catch {
    message.error("接口请求失败");
  } finally {
    loading.value = false;
  }
};

const handleSearch = () => {
  if (
    searchForm.startDate &&
    searchForm.endDate &&
    searchForm.startDate > searchForm.endDate
  ) {
    message.warning("开始日期不能晚于结束日期");
    return;
  }
  fetchData(1, pagination.pageSize);
};

const clearQuickDate = () => {
  quickDateValue.value = null;
};

const handleQuickDateChange = ({ target }) => {
  const option = quickDateOptions.find((item) => item.value === target.value);
  if (!option) return;
  searchForm.startDate = getDaysAgoDate(option.startOffset);
  searchForm.endDate = getDaysAgoDate(option.endOffset);
  handleSearch();
};

const handleReset = () => {
  searchForm.startDate = "";
  searchForm.endDate = "";
  searchForm.dimensionName = undefined;
  quickDateValue.value = null;
  fetchData(1, pagination.pageSize);
};

const handleTableChange = (pag) => {
  fetchData(pag.current, pag.pageSize);
};

/* ---------- CSV 下载 ---------- */
const handleDownload = async () => {
  downloading.value = true;
  try {
    const allData = [];
    let pageIdx = 1;
    let pageCount = 1;
    while (pageIdx <= pageCount) {
      const params = {
        pageIdx,
        pageSize: 200,
        startDate: searchForm.startDate || "",
        endDate: searchForm.endDate || "",
      };
      if (searchForm.dimensionName)
        params.dimensionName = searchForm.dimensionName;
      const json = await get(
        analyticsUrl("/order/stats/pay-refund/daily/report"),
        params
      );
      if (json.code !== 200) break;
      const d = json.data;
      allData.push(...(d.data ?? []));
      pageCount = d.pageCount;
      pageIdx++;
    }
    if (!allData.length) {
      message.warning("没有数据可下载");
      return;
    }

    const sortedData = sortRowsByDateAndDimension(fillMissingDimensions(allData));

    const headers = [
      "日期",
      "维度",
      "收款单数",
      "收款金额",
      "退款单数",
      "退款金额",
      "退款率",
      "小瑞退款单数",
      "小瑞退款金额",
      "小瑞退款率",
      "H5退款单数",
      "H5退款金额",
      "H5退款率",
      "淘金退款单数",
      "淘金退款金额",
      "淘金退款率",
      "梦莹退款单数",
      "梦莹退款金额",
      "梦莹退款率",
      "更新时间",
    ];
    const keys = [
      "statDate",
      "dimensionName",
      "payOrderCount",
      "payAmount",
      "refundOrderCount",
      "refundAmount",
      "refundRate",
      "adminRefundOrderCount",
      "adminRefundAmount",
      "adminRefundRate",
      "h5RefundOrderCount",
      "h5RefundAmount",
      "h5RefundRate",
      "taojinRefundOrderCount",
      "taojinRefundAmount",
      "taojinRefundRate",
      "mengyingRefundOrderCount",
      "mengyingRefundAmount",
      "mengyingRefundRate",
      "updatedAt",
    ];
    const amountFields = new Set([
      "payAmount",
      "refundAmount",
      "adminRefundAmount",
      "h5RefundAmount",
      "taojinRefundAmount",
      "mengyingRefundAmount",
    ]);
    const rateFields = new Set([
      "refundRate",
      "adminRefundRate",
      "h5RefundRate",
      "taojinRefundRate",
      "mengyingRefundRate",
    ]);

    const esc = (v) => {
      const s = String(v ?? "");
      return s.includes(",") || s.includes('"') || s.includes("\n")
        ? '"' + s.replace(/"/g, '""') + '"'
        : s;
    };

    const lines = [headers.map(esc).join(",")];
    for (const row of sortedData) {
      const cells = keys.map((k) => {
        const v = row[k];
        if (amountFields.has(k)) return esc((v / 100).toFixed(2));
        if (rateFields.has(k))
          return esc(v != null ? (parseFloat(v) * 100).toFixed(2) + "%" : "");
        return esc(v ?? "");
      });
      lines.push(cells.join(","));
    }

    const bom = "\uFEFF";
    const blob = new Blob([bom + lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `收退款-财务_${searchForm.startDate || "all"}_${searchForm.endDate || "all"
      }.csv`;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    message.error("下载失败");
  } finally {
    downloading.value = false;
  }
};

/* ---------- 初始化 ---------- */
onMounted(() => {
  fetchData();
});
</script>

<style scoped>
.quick-date-group {
  white-space: nowrap;
}
</style>
